import API from '@/api';
import V3API from '@/api/v3';
import { deleteCache, deleteCacheV3 } from '@/utils/axios';
import Vuetify from '@/plugins/vuetify';

const LOGIN = 'LOGIN';
const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
const LOGIN_FAILED = 'LOGIN_FAILED';
const LOGOUT = 'LOGOUT';

const GC_LOGIN = 'GC_LOGIN';
const GC_LOGIN_SUCCESS = 'GC_LOGIN_SUCCESS';
const GC_LOGIN_FAILED = 'GC_LOGIN_FAILED';
const GC_VERIFY_LOGIN_FAILED = 'GC_VERIFY_LOGIN_FAILED';
const GC_VERIFY_LOGIN_SUCCESS = 'GC_VERIFY_LOGIN_SUCCESS';
const RESET_PASSWORD_VERIFY_LOGIN_SUCCESS = 'RESET_PASSWORD_VERIFY_LOGIN_SUCCESS';

const USER_EXISTS_LOGIN = 'USER_EXISTS_LOGIN';
const USER_EXISTS_LOGIN_FAILED = 'USER_EXISTS_LOGIN_FAILED';
const USER_EXISTS_LOGIN_SUCCESS = 'USER_EXISTS_LOGIN_SUCCESS';

const WHITELIST_TOKEN = 'WHITELIST_TOKEN';
const WHITELIST_TOKEN_SUCCESS = 'WHITELIST_TOKEN_SUCCESS';
const WHITELIST_TOKEN_FAILED = 'WHITELIST_TOKEN_FAILED';

const CHECK_TOKEN = 'CHECK_TOKEN';
const CHECK_TOKEN_SUCCESS = 'CHECK_TOKEN_SUCCESS';
const CHECK_TOKEN_FAILED = 'CHECK_TOKEN_FAILED';

const REMOVE_AUTHENTICATION = 'REMOVE_AUTHENTICATION';

const SET_ADMIN_SITE = 'SET_ADMIN_SITE';

// * Enables the module to be namespaced
const namespaced = true;

// Authentication initial state
const state = {
  isAuthenticated: !!localStorage.getItem('token'),
  loginPending: false,
  gcLoginPending: false,
  userExistsLoading: false,
  tokenLoading: false,
  genLoading: false,
  whitelistToken: '',

  // ! Temp state for clubhouse testing
  // isClubHouseOwner: localStorage.getItem('type') === 'clubhouseOwner' || 'user',
  isClubHouseOwner: localStorage.getItem('type') === 'clubhouseOwner',
  adminSite: false,
};

// Authentication getters
const getters = {
  // * isAuthenticated returns a Boolean of if
  // * the user is authenticated or not
  isAuthenticated: (state) => {
    return state.isAuthenticated;
  },

  // * isLoading returns a Boolean of if
  // * loading is happening or not
  isLoading: (state) => {
    return state.loginPending;
  },

  isUserExistsLoading: (state) => {
    return state.userExistsLoading;
  },

  // ! Temp getter for clubhouse testing
  // isClubHouseOwner: state => {
  //   return state.isClubHouseOwner
  // }

  adminSite: (state) => {
    return state.adminSite;
  },

  getWhitelistToken: (state) => {
    return state.whitelistToken;
  },

  isLoadingVerification: (state) => {
    return state.gcLoginPending;
  },
};

// Authentication actions
const actions = {
  /**
   ** Login accepts a payload with a username and password. It
   ** will call the /authentications endpoint on the API. It will
   ** get the token from the response and set the `token` item in
   ** localStorage. Will resolve or reject with a Promise.
   * */
  async login({ commit }, payload) {
    let response;

    commit(LOGIN);

    try {
      response = await API.authentications.login(payload);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400 || response.status === 0) {
        commit(LOGIN_FAILED);
        reject(response);
      } else {
        localStorage.setItem('token', response.data.token);
        localStorage.removeItem('logoutAlert');

        // ! Temp setting storage user type for clubhouse testing
        localStorage.setItem('type', response.data.subjectType);
        commit(LOGIN_SUCCESS);
        resolve(response);
      }
    });
  },

  // * Logout removes the token from localStorage
  logout({ commit, dispatch }) {
    localStorage.removeItem('token');
    localStorage.removeItem('ghostToken');
    localStorage.removeItem('type');
    localStorage.removeItem('club');
    localStorage.removeItem('clubhouses');
    // localStorage.removeItem('logoutAlert');
    localStorage.removeItem('userPreferences');
    localStorage.removeItem('userUpdated');
    localStorage.removeItem('gcToken');
    localStorage.removeItem('eventQueueCode');
    localStorage.removeItem('sortScore');
    localStorage.removeItem('productStatusVisibility');
    localStorage.removeItem('seasonActiveTab');
    localStorage.removeItem('eventActiveTab');
    dispatch('users/resetState', null, { root: true });
    dispatch('shop/emptyCart', null, { root: true });
    dispatch('licenses/resetLicenses', null, { root: true });
    dispatch('shop/resetTax', null, { root: true });

    //* clear axios cache data on logout
    deleteCache();
    deleteCacheV3();

    commit(LOGOUT);
  },

  //* removeAuthentication sets authentication state to false
  removeAuthentication({ commit, dispatch }) {
    commit(REMOVE_AUTHENTICATION);
    dispatch('logout');
  },

  //* deleteToken deletes the whitelist token entry for the user id
  async deleteToken({ commit }, payload) {
    let response = {};

    commit(WHITELIST_TOKEN);

    try {
      response = await API.authentications.deleteToken(payload);
    } catch (error) {
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(WHITELIST_TOKEN_FAILED);
        reject(response);
      } else {
        commit(WHITELIST_TOKEN_SUCCESS);
        resolve();
      }
    });
  },

  //* createToken deletes the whitelist token entry for the user id
  async createToken({ commit }, payload) {
    let response = {};

    commit(WHITELIST_TOKEN);

    try {
      response = await API.authentications.createToken(payload);
    } catch (error) {
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(WHITELIST_TOKEN_FAILED);
        reject(response);
      } else {
        commit(WHITELIST_TOKEN_SUCCESS);
        resolve();
      }
    });
  },

  //* getToken gets the whitelist token entry for the user id
  async getToken({ commit }, payload) {
    let response = {};

    commit(WHITELIST_TOKEN);

    try {
      response = await API.authentications.getToken(payload);
    } catch (error) {
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(WHITELIST_TOKEN_FAILED);
        reject(response);
      } else {
        commit(WHITELIST_TOKEN_SUCCESS, response.data);
        resolve(response.data);
      }
    });
  },

  //* checkToken checks the validity of the user token
  async checkToken({ commit, dispatch }) {
    let response = {};
    commit(CHECK_TOKEN);

    try {
      response = await API.authentications.checkToken();
    } catch (error) {
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(CHECK_TOKEN_FAILED);
        reject(response);
      } else {
        commit(CHECK_TOKEN_SUCCESS);
        resolve(response);
      }
    });
  },

  // * Action to set admin site boolean
  setAdminSite({ commit }, payload) {
    commit(SET_ADMIN_SITE, payload);
  },

  //* V3 =============================================================

  async v3Login({ commit, dispatch }, payload) {
    dispatch('logout'); //* make sure it clears out old user cached data
    let response;

    commit(LOGIN);

    try {
      response = await V3API.authentications.v3Login(payload);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400 || response.status === 0) {
        commit(LOGIN_FAILED);
        reject(response);
      } else {
        localStorage.setItem('token', response.data.token);
        localStorage.removeItem('logoutAlert');

        // ! Temp setting storage user type for clubhouse testing - turned off
        // localStorage.setItem('type', response.data.subjectType);
        commit(LOGIN_SUCCESS);
        resolve(response);
      }
    });
  },

  //* calls /user_exists
  async userExists({ commit }, payload) {
    let response;

    commit(USER_EXISTS_LOGIN);

    try {
      response = await V3API.authentications.userExists(payload);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400 || response.status === 0) {
        commit(USER_EXISTS_LOGIN_FAILED);
        reject(response);
      } else {
        commit(USER_EXISTS_LOGIN_SUCCESS);
        resolve(response);
      }
    });
  },

  //* calls /user_verification_create
  async userVerification({ commit }, payload) {
    let response;

    commit(GC_LOGIN);

    try {
      response = await V3API.authentications.userVerification(payload);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400 || response.status === 0) {
        commit(GC_LOGIN_FAILED);
        reject(response);
      } else {
        commit(GC_LOGIN_SUCCESS);
        resolve(response);
      }
    });
  },

  // * calls /user_verification_login
  async userVerificationLogin({ commit }, payload) {
    let response;
    const { resetPassword = false } = payload;

    commit(GC_LOGIN);

    try {
      response = await V3API.authentications.userVerificationLogin(payload);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400 || response.status === 0) {
        commit(GC_VERIFY_LOGIN_FAILED);
        reject(response);
      } else {
        // console.log('response.data.token:', response.data.token);

        //* Disabling token and isAuthentication set when resetting password
        if (!resetPassword) {
          localStorage.setItem('token', response.data.token);
          commit(GC_VERIFY_LOGIN_SUCCESS);
        } else {
          commit(RESET_PASSWORD_VERIFY_LOGIN_SUCCESS);
        }
        localStorage.removeItem('logoutAlert');
        localStorage.setItem('userUpdated', true);
        resolve(response);
      }
    });
  },

  /**
   ** gameClientLogin accepts a payload with a game client token. It
   ** will call the /game_client_login endpoint on the API.
   ** After login and/or verification, it passes the game_client_token
   ** in payload and the user auth token in the header.
   * */
  async gameClientLogin({ commit }, payload) {
    let response;

    commit(GC_LOGIN);

    try {
      response = await V3API.authentications.gameClientLogin(payload);
      // console.log('response:', response);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400 || response.status === 0) {
        commit(GC_LOGIN_FAILED);
        reject(response);
      } else {
        commit(GC_LOGIN_SUCCESS);
        resolve(response);
      }
    });
  },

  setAuthenticatedFromToken({ commit }, token) {
    if (token) {
      localStorage.setItem('token', token);
      commit('SET_AUTHENTICATED', true);
    } else {
      localStorage.removeItem('token');
      commit('SET_AUTHENTICATED', false);
    }
  },

  // Alternative: just update the auth state without token management
  updateAuthenticationState({ commit }) {
    const token = localStorage.getItem('token');
    commit('SET_AUTHENTICATED', !!token);
  },
};

// Authentication mutations
const mutations = {
  // * LOGIN sets state for a login spinner
  [LOGIN](state) {
    state.loginPending = true;
  },

  // * LOGIN_SUCCESS sets state for the user to be authenticated
  // * and for the spinner to stop spinning
  [LOGIN_SUCCESS](state) {
    state.isAuthenticated = true;
    state.loginPending = false;

    // ! Temp state for clubhouse testing
    // state.isClubHouseOwner = localStorage.getItem('type') === 'clubhouseOwner'
  },

  // * LOGIN_FAILED stops spinner from showing
  [LOGIN_FAILED](state) {
    state.loginPending = false;
  },

  // * LOGOUT sets state to unauthenticate a user
  [LOGOUT](state) {
    state.isAuthenticated = false;

    // ! Temp state for clubhouse testing
    state.isClubHouseOwner = false;
    state.adminSite = false;
    Vuetify.framework.theme.isDark = false;
  },

  [WHITELIST_TOKEN](state) {
    state.tokenLoading = true;
  },

  [WHITELIST_TOKEN_SUCCESS](state, data) {
    state.tokenLoading = false;
    state.whitelistToken = data;
  },

  [WHITELIST_TOKEN_FAILED](state) {
    state.tokenLoading = false;
  },

  [CHECK_TOKEN](state) {
    state.genLoading = true;
  },

  [CHECK_TOKEN_SUCCESS](state) {
    state.genLoading = false;
  },

  [CHECK_TOKEN_FAILED](state) {
    state.genLoading = false;
  },

  [REMOVE_AUTHENTICATION](state) {
    state.isAuthenticated = false;
  },

  [SET_ADMIN_SITE](state, value) {
    state.adminSite = value;
  },

  // * GC_LOGIN sets state for a login spinner
  [GC_LOGIN](state) {
    state.gcLoginPending = true;
  },

  // * GC_LOGIN_SUCCESS sets state for the user to be authenticated
  // * and for the spinner to stop spinning
  [GC_LOGIN_SUCCESS](state) {
    state.gcLoginPending = false;
  },

  // * GC_LOGIN_FAILED stops spinner from showing
  [GC_LOGIN_FAILED](state) {
    state.gcLoginPending = false;
  },

  // * USER_EXISTS_LOGIN sets state for a login spinner
  [USER_EXISTS_LOGIN](state) {
    state.userExistsLoading = true;
  },

  // * USER_EXISTS_LOGIN_FAILED stops spinner from showing
  [USER_EXISTS_LOGIN_FAILED](state) {
    state.userExistsLoading = false;
  },

  // * USER_EXISTS_LOGIN_SUCCESS sets state for the user to be authenticated
  // * and for the spinner to stop spinning
  [USER_EXISTS_LOGIN_SUCCESS](state) {
    state.userExistsLoading = false;
  },

  // * GC_LOGIN_SUCCESS sets state for the user to be authenticated
  // * and for the spinner to stop spinning
  [GC_VERIFY_LOGIN_SUCCESS](state) {
    state.isAuthenticated = true;
    state.gcLoginPending = false;
  },

  // * RESET_PASSWORD_VERIFY_LOGIN_SUCCESS bypasses setting state for the user to be authenticated
  // * and for the spinner to stop spinning
  [RESET_PASSWORD_VERIFY_LOGIN_SUCCESS](state) {
    state.isAuthenticated = false;
    state.gcLoginPending = false;
  },

  // * GC_LOGIN_FAILED stops spinner from showing
  [GC_VERIFY_LOGIN_FAILED](state) {
    state.gcLoginPending = false;
  },

  SET_AUTHENTICATED(state, isAuthenticated) {
    state.isAuthenticated = isAuthenticated;
  },

  SET_TOKEN_FROM_URL(state, token) {
    state.isAuthenticated = !!token;
    // Might also want to store the token in state if needed
  },
};

export default {
  namespaced,
  state,
  actions,
  getters,
  mutations,
};
